// Branch-aware, two-sided Openings Academy player. Reviewed course moves drive
// assessment and progress; a lazy CC0 theory graph supplies broader, explicitly
// labelled exploration choices without pretending they are editorially reviewed.

import { Chessground } from "chessground";
import type { Api as CgApi } from "chessground/api";
import type { Key } from "chessground/types";

import { esc, h } from "../dom.ts";
import { getLang, t } from "../i18n/index.ts";
import { navigate } from "../router.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { legalDests, parsePosition, uciSquares } from "../game/board.ts";
import { buildReview, childNode, type GameNode } from "../game/review.ts";
import { courseCoverage, saveCompletion } from "../game/openingProgress.ts";
import {
  cachedBookPosition,
  loadOpeningBook,
  lookupBookPosition,
  type BookMove,
  type BookPosition,
} from "../game/openingBook.ts";
import { RunTimers } from "../game/runTimers.ts";
import { warmEngineAssets } from "../engine/stockfish.ts";
import {
  assessCourseMove,
  buildCourseTree,
  courseLeafPaths,
  courseMainline,
  findCourseForBookOpening,
  OPENING_COURSES,
  rewindToFork,
  selectCourseTarget,
  selectNextBranchTarget,
  targetCourseChoice,
  type CourseNode,
  type CourseShape,
  type OpeningCourse,
} from "../data/openingCourses.ts";
import type { Color } from "../game/types.ts";

const OPPONENT_MOVE_MS = 450;
/** Reading pause between a correct learner move and the automatic reply. */
const FEEDBACK_MS = 900;
const SNAP_BACK_MS = 650;

export function openingCourseScreen(slug: string, requestedSide?: string): HTMLElement {
  // Study lives in this browser: guests and members alike, no member login.
  const course = OPENING_COURSES.find((candidate) => candidate.slug === slug);
  if (!course || (requestedSide !== undefined && requestedSide !== "white" && requestedSide !== "black")) {
    return h(`
      <main class="card">
        <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
        <p class="msg bad">${esc(t("openings.unknownCourse"))}</p>
        <p><a class="btn ghost" data-link href="/study">${t("openings.backToStudy")}</a></p>
      </main>
    `);
  }

  const side: Color = requestedSide ?? course.color;
  const el = buildLayout(course, side);
  requestAnimationFrame(() => {
    if (!el.isConnected) return;
    const ctrl = new CourseCtrl(el, course, side);
    onDisconnect(el, () => ctrl.destroy());
  });
  return el;
}

/** `feedback` is a short timed reading pause after a correct learner move;
 *  the opponent replies automatically — there is no per-move confirm gate. */
type CourseState = "intro" | "opponent" | "learner" | "feedback" | "done";
type EndReason = "line" | "course" | "book" | "blocked" | null;

class CourseCtrl {
  private cg: CgApi;
  private readonly roots: CourseNode[];
  private readonly leafPaths: string[];
  private readonly timers = new RunTimers(window);
  private readonly reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  private completed: Set<string>;
  private path: CourseNode[] = [];
  private positions: GameNode[] = [buildReview([]).nodes[0]];
  private state: CourseState = "intro";
  private endReason: EndReason = null;
  private targetPath: string | undefined;
  private wrongTries = 0;
  /** Supported continuations are concealed until the learner asks once. */
  private hintStage: 0 | 1 = 0;
  private activeBook: BookPosition | null = null;
  private relatedCourse: OpeningCourse | undefined;
  private bookRequest = 0;
  private destroyed = false;

  constructor(
    private root: HTMLElement,
    private course: OpeningCourse,
    private side: Color,
  ) {
    this.roots = buildCourseTree(course);
    this.leafPaths = courseLeafPaths(course);
    this.completed = new Set(courseCoverage(course.slug, side, this.leafPaths).lines);

    const boardEl = root.querySelector<HTMLElement>(".cg-board-wrap")!;
    this.cg = Chessground(boardEl, {
      fen: this.position().fen,
      orientation: side,
      highlight: { lastMove: true, check: true },
      animation: { enabled: !this.reducedMotion, duration: this.reducedMotion ? 0 : 200 },
      movable: {
        free: false,
        color: undefined,
        dests: new Map(),
        showDests: true,
        events: { after: (orig, dest) => this.onUserMove(orig, dest) },
      },
      premovable: { enabled: false },
      draggable: { showGhost: true },
      drawable: { enabled: false, visible: true },
    });

    this.wireActions();
    this.showBoard();
    this.showIntro();
    this.paintProgress();
    void loadOpeningBook();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.timers.destroy();
    this.cg.destroy();
  }

  private position(): GameNode {
    return this.positions[this.positions.length - 1];
  }

  private choices(): CourseNode[] {
    return this.path.length ? this.path[this.path.length - 1].children : this.roots;
  }

  private acceptedChoices(): CourseNode[] {
    return this.choices().filter((node) => !node.reject);
  }

  private targetChoice(): CourseNode | undefined {
    return targetCourseChoice(this.choices(), this.targetPath, this.path.length);
  }

  private showIntro(): void {
    this.state = "intro";
    this.endReason = null;
    this.relatedCourse = undefined;
    this.setPrompt("");
    this.setCoach(
      `${t(this.side === "white" ? "openings.trainingWhite" : "openings.trainingBlack")} ${this.course.intro[getLang()]}`,
      "",
      undefined,
      this.course.name[getLang()],
    );
    this.setCoachAside(`${t("openings.mainLine")} ${courseMainline(this.course, 10)}`);
    this.lockBoard();
    this.clearChoices();
    this.syncActions();
  }

  private start(): void {
    if (this.state !== "intro") return;
    this.targetPath = selectCourseTarget(this.leafPaths, this.completed);
    this.proceed();
  }

  private proceed(asReply = false): void {
    if (this.destroyed) return;
    if (this.acceptedChoices().length === 0) {
      this.finish(true);
      return;
    }
    if (this.position().turn === this.side) this.learnerTurn();
    else this.opponentMove(asReply);
  }

  /** `asReply` appends the move to the learner's feedback instead of replacing
   *  it, so both halves of the exchange stay readable together. */
  private opponentMove(asReply = false): void {
    this.state = "opponent";
    this.lockBoard();
    this.clearChoices();
    this.syncActions();
    this.after(asReply ? 0 : OPPONENT_MOVE_MS, () => {
      const node = this.targetChoice() ?? this.acceptedChoices()[0];
      if (!node || !this.playNode(node)) {
        this.state = "done";
        this.endReason = "blocked";
        this.setCoach(t("openings.lineUnavailable"), "bad");
        this.syncActions();
        return;
      }
      if (asReply) this.setCoachReply(node);
      else this.setCoachForOpponent(node);
      this.proceed();
    });
  }

  private leavesBelow(node: CourseNode): string[] {
    if (node.reject) return [];
    const children = node.children.filter((child) => !child.reject);
    return children.length ? children.flatMap((child) => this.leavesBelow(child)) : [node.pathKey];
  }

  private learnerTurn(): void {
    this.state = "learner";
    this.endReason = null;
    this.wrongTries = 0;
    this.hintStage = 0;
    this.setPrompt(t("openings.yourMove"));
    this.unlockBoard();
    this.syncActions();
    this.renderChoices();
  }

  private onUserMove(orig: Key, dest: Key): void {
    if (this.state !== "learner") return;
    this.classifyMove(orig + dest);
  }

  private classifyMove(uci: string): void {
    const current = this.position();
    const played = childNode(current, uci);
    if (!played) {
      this.showBoard();
      this.unlockBoard();
      return;
    }
    const authored = this.choices().find((choice) => childNode(current, choice.uci)?.fen === played.fen);
    if (authored?.reject) this.nearMiss(authored, played.san ?? uci);
    else if (authored) this.correct(authored);
    else {
      const bookMove = this.activeBook?.moves.find((move) => move.uci === (played.uci ?? uci));
      const related = findCourseForBookOpening(bookMove?.resultingOpening);
      if (bookMove && related && related.slug !== this.course.slug) this.exploreBookMove(bookMove, played, related);
      else this.wrong(played.san ?? uci);
    }
  }

  private correct(node: CourseNode): void {
    const siblings = this.choices();
    const expected = this.targetChoice();
    const switched = Boolean(expected && expected !== node);
    if (switched) {
      this.targetPath = selectCourseTarget(this.leavesBelow(node), this.completed, () => 0);
    }
    this.state = "feedback";
    if (!this.playNode(node)) return;
    this.lockBoard();
    this.setPrompt("");
    this.clearChoices();
    // A leaf finishes immediately — finish() carries the move's explanation.
    if (this.acceptedChoices().length === 0) {
      this.finish(true);
      return;
    }
    this.setFeedbackFor(node, siblings, switched);
    this.syncActions();
    this.after(FEEDBACK_MS, () => this.proceed(true));
  }

  private nearMiss(node: CourseNode, playedSan: string): void {
    this.state = "opponent";
    this.lockBoard();
    this.clearChoices();
    const assessment = assessCourseMove(node, this.choices());
    this.setCoach(
      node.reject?.[getLang()] ?? t("openings.wrong"),
      "bad",
      node.shapes,
      `${playedSan} — ${this.course.name[getLang()]} · ${t(`openings.verdict.${assessment.verdict}`)}`,
    );
    this.syncActions();
    this.after(SNAP_BACK_MS, () => this.restoreLearner());
  }

  private wrong(playedSan: string): void {
    this.state = "opponent";
    this.wrongTries += 1;
    this.lockBoard();
    this.clearChoices();
    this.setCoach(
      t("openings.notCovered", { move: playedSan }),
      "bad",
      undefined,
      `${playedSan} — ${t("openings.verdict.unreviewed")}`,
    );
    this.syncActions();
    this.after(SNAP_BACK_MS, () => {
      this.showBoard();
      this.state = "learner";
      this.unlockBoard();
      if (this.wrongTries >= 2 && this.hintStage === 0) this.revealSupported();
      this.syncActions();
      this.renderChoices();
    });
  }

  private restoreLearner(): void {
    this.showBoard();
    this.state = "learner";
    this.unlockBoard();
    this.syncActions();
    this.renderChoices();
  }

  private playNode(node: CourseNode): boolean {
    const next = childNode(this.position(), node.uci);
    if (!next) return false;
    this.path.push(node);
    this.positions.push(next);
    this.showBoard();
    this.renderMoves();
    return true;
  }

  private exploreBookMove(move: BookMove, played: GameNode, related: OpeningCourse): void {
    this.state = "done";
    this.endReason = "book";
    this.relatedCourse = related;
    this.positions.push(played);
    this.showBoard();
    this.renderMoves();
    this.lockBoard();
    this.setPrompt("");
    this.clearChoices();
    const opening = move.resultingOpening?.name ?? this.course.name[getLang()];
    this.setCoach(
      t("openings.bookAlternativeHelp", { opening }),
      "",
      undefined,
      `${move.san} — ${opening} · ${t("openings.verdict.playable")}`,
    );
    this.paintProgress();
    this.syncActions();
  }

  private finish(justCompleted: boolean): void {
    this.state = "done";
    this.lockBoard();
    this.clearChoices();
    this.setPrompt("");
    const leaf = this.path[this.path.length - 1];
    let coverage = courseCoverage(this.course.slug, this.side, this.leafPaths);
    if (justCompleted && leaf) {
      coverage = saveCompletion(this.course.slug, this.side, leaf.pathKey, this.leafPaths);
      this.completed = new Set(coverage.lines);
    }
    this.endReason = coverage.complete ? "course" : "line";
    // The closing move's explanation leads; the course recipe is a quieter
    // aside so replaying lines doesn't re-preach the whole summary.
    const completion = coverage.complete ? t("openings.courseCompleted") : t("openings.lineCompleted");
    this.setCoach(
      [leaf?.explain?.[getLang()], completion].filter(Boolean).join(" "),
      "good",
      leaf?.shapes,
      coverage.complete ? t("openings.courseCompleteTitle") : t("openings.lineCompleteTitle"),
    );
    this.setCoachAside(this.course.summary[getLang()]);
    this.paintProgress();
    this.syncActions();
  }

  private restart(): void {
    this.resetRun();
    this.showIntro();
  }

  private nextLine(): void {
    if (this.state !== "done" || (this.endReason !== "line" && this.endReason !== "course")) return;
    if (this.leafPaths.length < 2) return;
    const current = this.path[this.path.length - 1]?.pathKey;
    const target = selectNextBranchTarget(this.leafPaths, this.completed, current);
    if (!target) return;
    const keep = rewindToFork(this.path.map((node) => node.uci), target);
    this.timers.reset();
    this.bookRequest++;
    this.path = this.path.slice(0, keep);
    this.positions = this.positions.slice(0, keep + 1);
    this.targetPath = target;
    this.endReason = null;
    this.relatedCourse = undefined;
    this.wrongTries = 0;
    this.hintStage = 0;
    this.activeBook = null;
    this.showBoard();
    this.renderMoves();
    this.clearChoices();
    this.setCoach(t("openings.resumeFromFork"), "", undefined, this.course.name[getLang()]);
    this.proceed();
  }

  private resetRun(): void {
    this.timers.reset();
    this.bookRequest++;
    this.path = [];
    this.positions = [buildReview([]).nodes[0]];
    this.endReason = null;
    this.relatedCourse = undefined;
    this.targetPath = undefined;
    this.wrongTries = 0;
    this.hintStage = 0;
    this.activeBook = null;
    this.showBoard();
    this.renderMoves();
    this.paintProgress();
    this.clearChoices();
  }

  private hint(): void {
    this.revealSupported();
  }

  /** Reveal one or many accepted continuations without making a move. */
  private revealSupported(): void {
    if (this.state !== "learner" || this.hintStage !== 0 || this.acceptedChoices().length === 0) return;
    this.hintStage = 1;
    this.setCoach(t("openings.supportedHelp"), "", undefined, t("openings.supportedMoves"));
    this.paintChoices();
    this.syncActions();
  }

  private takeBack(): void {
    if (this.state !== "done" || this.endReason !== "book" || this.positions.length <= 1) return;
    this.timers.reset();
    this.positions.pop();
    this.state = "learner";
    this.endReason = null;
    this.relatedCourse = undefined;
    this.wrongTries = 0;
    this.hintStage = 0;
    this.activeBook = null;
    this.showBoard();
    this.renderMoves();
    this.setCoach(t("openings.backInCourse"), "", undefined, this.course.name[getLang()]);
    this.unlockBoard();
    this.syncActions();
    this.renderChoices();
  }

  private openRelatedCourse(): void {
    if (this.state !== "done" || this.endReason !== "book" || !this.relatedCourse) return;
    navigate(`/study/openings/${encodeURIComponent(this.relatedCourse.slug)}/${this.side}`);
  }

  private playVsComputer(): void {
    sessionStorage.setItem(
      "chess.pendingOpening",
      JSON.stringify({
        slug: this.course.slug,
        color: this.side,
        moves: this.positions.slice(1).map((node) => node.uci),
      }),
    );
    navigate("/play/computer");
  }

  private showBoard(): void {
    const node = this.position();
    this.cg.set({
      fen: node.fen,
      lastMove: node.uci ? uciSquares(node.uci) : undefined,
      turnColor: node.turn,
      movable: { color: undefined, dests: new Map() },
    });
    this.cg.setAutoShapes([]);
  }

  private lockBoard(): void {
    this.cg.set({ movable: { color: undefined, dests: new Map() } });
  }

  private unlockBoard(): void {
    const node = this.position();
    const pos = parsePosition(node.fen);
    this.cg.set({
      turnColor: node.turn,
      movable: { color: this.side, dests: pos ? legalDests(pos) : new Map() },
    });
  }

  private openingName(node: CourseNode): string {
    const lang = getLang();
    const bookMove = this.activeBook?.moves.find((move) => move.uci === node.uci);
    return node.name?.[lang] ?? bookMove?.resultingOpening?.name ?? this.course.name[lang];
  }

  // Routine correct moves stay tonally neutral — the chess content is the
  // reward; green is reserved for completing a line or the course.
  private setFeedbackFor(node: CourseNode, siblings: CourseNode[], switched: boolean): void {
    const assessment = assessCourseMove(node, siblings);
    const opening = this.openingName(node);
    const rank = assessment.total > 1 ? t("openings.choiceRank", { n: assessment.index, total: assessment.total }) : "";
    const switchNote = switched ? t("openings.switchedVariation") : "";
    const explanation = node.explain?.[getLang()] ?? t("openings.supportedContinuation");
    this.setCoach(
      [switchNote, explanation].filter(Boolean).join(" "),
      "",
      node.shapes,
      [node.san, opening, t(`openings.verdict.${assessment.verdict}`), rank].filter(Boolean).join(" · "),
    );
  }

  private setCoachForOpponent(node: CourseNode): void {
    this.setCoach(
      node.explain?.[getLang()] ?? t("openings.supportedContinuation"),
      "",
      node.shapes,
      `${node.san} — ${this.openingName(node)} · ${t("openings.opponentReply")}`,
    );
  }

  /** Append the opponent's reply under the learner's feedback; the reply's
   *  shapes (if any) take over the board illustration. */
  private setCoachReply(node: CourseNode): void {
    const lang = getLang();
    const name = node.name?.[lang];
    const explain = node.explain?.[lang];
    this.setCoachAside(
      [`${node.san} · ${t("openings.opponentReply")}`, name, explain].filter(Boolean).join(" — "),
    );
    if (node.shapes) {
      this.cg.setAutoShapes(
        node.shapes.map((shape) => ({
          orig: shape.orig as Key,
          dest: shape.dest as Key | undefined,
          brush: shape.brush,
        })),
      );
    }
  }

  private setCoach(
    text: string,
    tone: "" | "good" | "bad" = "",
    shapes?: CourseShape[],
    title = "",
  ): void {
    const box = this.root.querySelector<HTMLElement>(".coach")!;
    box.className = `coach panel${tone ? ` ${tone}` : ""}`;
    const titleEl = box.querySelector<HTMLElement>(".coach-title")!;
    titleEl.textContent = title;
    titleEl.hidden = !title;
    box.querySelector<HTMLElement>(".coach-text")!.textContent = text;
    this.setCoachAside("");
    this.cg.setAutoShapes(
      (shapes ?? []).map((shape) => ({
        orig: shape.orig as Key,
        dest: shape.dest as Key | undefined,
        brush: shape.brush,
      })),
    );
  }

  /** Secondary coach line (opponent reply, main-line preview, course recipe). */
  private setCoachAside(text: string): void {
    const aside = this.root.querySelector<HTMLElement>(".coach-reply")!;
    aside.textContent = text;
    aside.hidden = !text;
  }

  private setPrompt(text: string): void {
    const prompt = this.root.querySelector<HTMLElement>(".turn-prompt")!;
    prompt.textContent = text;
    prompt.hidden = !text;
  }

  private paintProgress(): void {
    const coverage = courseCoverage(this.course.slug, this.side, this.leafPaths);
    this.completed = new Set(coverage.lines);
    const line = this.root.querySelector<HTMLElement>(".progress-line")!;
    line.textContent = `${coverage.complete ? "✓ " : ""}${t("openings.linesCovered", { n: coverage.covered, total: coverage.total })}`;
    this.root.querySelector<HTMLElement>(".course-progress .fill")!.style.width =
      `${coverage.total ? Math.round((coverage.covered / coverage.total) * 100) : 0}%`;
  }

  private renderMoves(): void {
    const list = this.root.querySelector<HTMLElement>(".movelist")!;
    list.replaceChildren();
    for (let index = 1; index < this.positions.length; index += 2) {
      const row = document.createElement("div");
      row.className = "move-row";
      const white = this.positions[index]?.san ?? "";
      const black = this.positions[index + 1]?.san ?? "";
      row.innerHTML =
        `<span class="mn">${(index - 1) / 2 + 1}.</span>` +
        `<span class="san">${esc(white)}</span><span class="san">${esc(black)}</span>`;
      list.append(row);
    }
    list.scrollTop = list.scrollHeight;
  }

  private renderChoices(): void {
    if (this.state !== "learner") return;
    const fen = this.position().fen;
    const request = ++this.bookRequest;
    this.activeBook = cachedBookPosition(fen);
    this.paintChoices();
    if (this.activeBook) return;
    void lookupBookPosition(fen).then((position) => {
      if (this.destroyed || request !== this.bookRequest || this.state !== "learner" || this.position().fen !== fen) return;
      this.activeBook = position;
      this.paintChoices();
    });
  }

  private paintChoices(): void {
    const panel = this.root.querySelector<HTMLElement>(".course-choices")!;
    const list = panel.querySelector<HTMLElement>(".move-choice-list")!;
    const authored = this.acceptedChoices();
    const authoredUcis = new Set(authored.map((node) => node.uci));
    const bookOnly = (this.activeBook?.moves ?? [])
      .map((move) => ({ move, course: findCourseForBookOpening(move.resultingOpening) }))
      .filter(({ move, course }) => !authoredUcis.has(move.uci) && course && course.slug !== this.course.slug);
    const items: { uci: string; san: string; meta: string; title?: string; book: boolean }[] = authored.map((node) => {
      const assessment = assessCourseMove(node, this.choices());
      return {
        uci: node.uci,
        san: node.san,
        meta: this.course.kind === "supported"
          ? t("openings.supported")
          : `${t("openings.supported")} · ${t(`openings.role.${assessment.role}`)}`,
        title: node.name?.[getLang()],
        book: false,
      };
    });
    for (const { move, course } of bookOnly) {
      if (items.length >= 6) break;
      items.push({
        uci: move.uci,
        san: move.san,
        meta: t("openings.supportedOpening"),
        title: course?.name[getLang()] ?? move.resultingOpening?.name,
        book: true,
      });
    }
    list.replaceChildren();
    for (const item of items) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `move-choice${item.book ? " book" : ""}`;
      button.dataset.uci = item.uci;
      button.innerHTML = `<strong>${esc(item.san)}</strong><span>${esc(item.meta)}</span>${item.title ? `<small>${esc(item.title)}</small>` : ""}`;
      list.append(button);
    }
    const hasBook = items.some((item) => item.book);
    panel.querySelector<HTMLElement>(".candidate-help")!.textContent = hasBook
      ? `${t("openings.candidateHelp")} ${t("openings.candidateHelpBook")}`
      : t("openings.candidateHelp");
    panel.querySelector<HTMLElement>(".book-credit")!.hidden = !hasBook;
    panel.hidden = this.state !== "learner" || this.hintStage === 0 || items.length === 0;
    if (this.state === "learner" && this.hintStage === 1) {
      this.cg.setAutoShapes(items.map((item) => ({
        orig: item.uci.slice(0, 2) as Key,
        dest: item.uci.slice(2, 4) as Key,
        brush: "paleGrey",
      })));
    }
  }

  private clearChoices(): void {
    this.bookRequest++;
    this.activeBook = null;
    const panel = this.root.querySelector<HTMLElement>(".course-choices")!;
    panel.hidden = true;
    panel.querySelector<HTMLElement>(".move-choice-list")!.replaceChildren();
  }

  private syncActions(): void {
    const button = (action: string) => this.root.querySelector<HTMLButtonElement>(`[data-act="${action}"]`)!;
    button("start").hidden = this.state !== "intro";
    const hint = button("hint");
    hint.hidden = this.state !== "learner" || this.hintStage === 1;
    hint.textContent = t("openings.showSupported");
    const showBot = this.state === "done" && this.positions.length > 1;
    button("play-bot").hidden = !showBot;
    if (showBot) void warmEngineAssets();
    // Next branch only when the course has real alternatives (2+ leaves).
    // After full coverage it still cycles 0 → 1 → 2 → … → 0 in catalog order.
    const canNextBranch =
      this.state === "done" &&
      (this.endReason === "line" || this.endReason === "course") &&
      this.leafPaths.length >= 2;
    button("next-line").hidden = !canNextBranch;
    button("take-back").hidden = this.state !== "done" || this.endReason !== "book";
    const related = button("related-course");
    related.hidden = this.state !== "done" || this.endReason !== "book" || !this.relatedCourse;
    if (this.relatedCourse) related.textContent = t("openings.studyOpening", { opening: this.relatedCourse.name[getLang()] });
    button("restart").hidden = this.state === "intro";
    button("restart").textContent = this.state === "done" ? t("openings.replay") : t("openings.restart");
  }

  private wireActions(): void {
    const on = (action: string, fn: () => void) =>
      this.root.querySelector<HTMLButtonElement>(`[data-act="${action}"]`)!.addEventListener("click", fn);
    on("start", () => this.start());
    on("hint", () => this.hint());
    on("next-line", () => this.nextLine());
    on("take-back", () => this.takeBack());
    on("related-course", () => this.openRelatedCourse());
    on("restart", () => this.restart());
    on("play-bot", () => this.playVsComputer());
    this.root.querySelector<HTMLElement>(".move-choice-list")!.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-uci]");
      if (button && this.state === "learner") this.classifyMove(button.dataset.uci!);
    });
  }

  private after(ms: number, fn: () => void): void {
    this.timers.after(this.reducedMotion ? 0 : ms, fn);
  }
}

function buildLayout(course: OpeningCourse, side: Color): HTMLElement {
  const lang = getLang();
  const coursePath = `/study/openings/${encodeURIComponent(course.slug)}`;
  const sideLink = (color: Color): string => `
    <a class="btn ghost sm${side === color ? " active" : ""}" data-link
       href="${coursePath}/${color}" aria-current="${side === color ? "page" : "false"}">
      ${t(`chess.${color}`)}
    </a>`;
  return h(`
    <main class="game course">
      <div class="board-col"><div class="cg-board-wrap"></div></div>
      <aside class="side">
        <div class="side-top">
          <span class="title">${esc(course.name[lang])} · ${esc(course.eco)}</span>
          <a class="btn ghost" data-link href="/study">${t("openings.backToStudy")}</a>
        </div>
        <div class="course-side-picker" aria-label="${t("openings.chooseSide")}">
          <span class="fine">${t("openings.trainAs")}</span>${sideLink("white")}${sideLink("black")}
        </div>
        <div class="course-progress"><div class="fill"></div></div>
        <p class="progress-line fine"></p>
        <div class="coach panel" role="status" aria-live="polite" aria-atomic="true">
          <p class="coach-title" hidden></p><p class="coach-text"></p>
          <p class="coach-reply" hidden></p>
        </div>
        <p class="turn-prompt" role="status" aria-live="polite" hidden></p>
        <section class="course-choices panel" aria-labelledby="move-choices-title" hidden>
          <h3 id="move-choices-title">${t("openings.supportedMoves")}</h3>
          <p class="fine candidate-help">${t("openings.candidateHelp")}</p>
          <div class="move-choice-list"></div>
          <p class="book-credit fine">${t("openings.bookCredit")} <a href="https://github.com/lichess-org/chess-openings" target="_blank" rel="noreferrer">lichess-org/chess-openings · CC0</a></p>
        </section>
        <div class="controls course-actions">
          <button class="btn primary" data-act="start" type="button">${t("openings.start")}</button>
          <button class="btn ghost" data-act="hint" type="button" hidden>${t("openings.showSupported")}</button>
          <button class="btn primary" data-act="next-line" type="button" hidden>${t("openings.nextLine")}</button>
          <button class="btn primary" data-act="take-back" type="button" hidden>${t("openings.takeBack")}</button>
          <button class="btn ghost" data-act="related-course" type="button" hidden></button>
          <button class="btn ghost" data-act="play-bot" type="button" hidden>${t("openings.playVsComputer")}</button>
          <button class="btn ghost" data-act="restart" type="button" hidden>${t("openings.restart")}</button>
        </div>
        <p class="fine local-progress-note">${t("openings.localProgress")}</p>
        <div class="movelist course-moves"></div>
      </aside>
    </main>
  `);
}
